import { isValidEmail } from '@lender/utils';
import _ from 'lodash';

import { PendingChangesActionType } from 'constants/pendingChanges';
import { prepareStringForComparison } from 'utils/data';

import {
  CUSTOM_FIELDS_BOOLEAN_STRING,
  CUSTOM_FIELDS_TARGET,
  CUSTOM_FIELDS_VALIDATION_ERROR,
  CUSTOM_FIELDS_VALUE_SOURCE,
  CUSTOM_FIELDS_VALUE_TYPE,
} from './CustomFieldsSection.constants';

const CUSTOM_FIELDS_TARGET_ID_KEY = {
  [CUSTOM_FIELDS_TARGET.LOAN]: 'applicationId',
  [CUSTOM_FIELDS_TARGET.BORROWER]: 'borrowerId',
  [CUSTOM_FIELDS_TARGET.PROPERTY]: 'propertyId',
  [CUSTOM_FIELDS_TARGET.SUBJECT_PROPERTY]: 'propertyId',
  [CUSTOM_FIELDS_TARGET.INCOME]: 'incomeId',
  [CUSTOM_FIELDS_TARGET.ASSET]: 'assetId',
  [CUSTOM_FIELDS_TARGET.LIABILITY]: 'liabilityId',
  [CUSTOM_FIELDS_TARGET.NBE]: 'nonBorrowerEntityId',
};

export const getTargetIdFromIdentifier = ({ targetIdentifier, target }) => {
  const key = CUSTOM_FIELDS_TARGET_ID_KEY[target];

  return key ? (targetIdentifier?.[key] ?? null) : null;
};

const normalizeTargets = target => {
  if (_.isNil(target)) {
    return [];
  }

  return _.isArray(target) ? target : [target];
};

const URL_VALIDATION_REGEXP = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(\/[\w\-./?%&=]*)?$/i;

const PHONE_DIGITS_MIN_LENGTH = 10;

const PHONE_DIGITS_MAX_LENGTH = 15;

export const isCalculated = definition =>
  definition?.valueSource?.type === CUSTOM_FIELDS_VALUE_SOURCE.CALCULATED;

export const isCalculatedDisplayableInLoanEditor = definition =>
  isCalculated(definition) && !!definition?.valueSource?.displayInLoanEditor;

export const isManualEnabled = definition =>
  definition?.valueSource?.type === CUSTOM_FIELDS_VALUE_SOURCE.MANUAL;

export const filterDisplayableEnabledDefinitionsForTarget = (definitions, target) => {
  const targets = normalizeTargets(target);

  if (!targets.length) {
    return [];
  }

  const targetSet = new Set(targets);

  return (definitions ?? []).filter(definition => {
    if (!definition?.isEnabled) {
      return false;
    }

    if (!targetSet.has(definition?.target)) {
      return false;
    }

    if (isManualEnabled(definition)) {
      return true;
    }

    return isCalculatedDisplayableInLoanEditor(definition);
  });
};

export const findCurrentValueForDefinition = ({ values, definitionId, target, targetEntityId }) =>
  (values ?? []).find(
    value =>
      value?.customFieldDefinitionId === definitionId &&
      prepareStringForComparison(
        getTargetIdFromIdentifier({ targetIdentifier: value?.targetIdentifier, target }),
      ) === prepareStringForComparison(targetEntityId),
  );

export const isManualReadOnly = definition => !!definition?.valueSource?.readOnly;

export const isManualRequired = definition => !!definition?.valueSource?.isRequired;

export const isValueEmpty = value => _.isNil(value) || value === '';

const validateEmailValue = value => {
  if (isValueEmpty(value)) {
    return null;
  }

  return isValidEmail(String(value)) ? null : CUSTOM_FIELDS_VALIDATION_ERROR.EMAIL;
};

const validatePhoneValue = value => {
  if (isValueEmpty(value)) {
    return null;
  }

  const digits = String(value).replace(/\D/g, '');

  if (digits.length < PHONE_DIGITS_MIN_LENGTH || digits.length > PHONE_DIGITS_MAX_LENGTH) {
    return CUSTOM_FIELDS_VALIDATION_ERROR.PHONE;
  }

  return null;
};

const validateUrlValue = value => {
  if (isValueEmpty(value)) {
    return null;
  }

  return URL_VALIDATION_REGEXP.test(value) ? null : CUSTOM_FIELDS_VALIDATION_ERROR.URL;
};

const VALUE_TYPE_TO_FORMAT_VALIDATOR = {
  [CUSTOM_FIELDS_VALUE_TYPE.EMAIL]: validateEmailValue,
  [CUSTOM_FIELDS_VALUE_TYPE.PHONE]: validatePhoneValue,
  [CUSTOM_FIELDS_VALUE_TYPE.URL]: validateUrlValue,
};

export const validateValueFormat = ({ valueType, value }) => {
  const validator = VALUE_TYPE_TO_FORMAT_VALIDATOR[valueType];

  return validator ? validator(value) : null;
};

const STRING_BOOLEAN_TO_BOOLEAN = {
  [CUSTOM_FIELDS_BOOLEAN_STRING.TRUE]: true,
  [CUSTOM_FIELDS_BOOLEAN_STRING.FALSE]: false,
};

export const coerceBooleanFromString = stringValue =>
  STRING_BOOLEAN_TO_BOOLEAN[stringValue] ?? false;

export const coerceBooleanToString = booleanValue =>
  booleanValue ? CUSTOM_FIELDS_BOOLEAN_STRING.TRUE : CUSTOM_FIELDS_BOOLEAN_STRING.FALSE;

export const buildInitialFormValues = ({ definitions, values, targetEntityId }) =>
  (definitions ?? []).reduce((acc, definition) => {
    const current = findCurrentValueForDefinition({
      values,
      definitionId: definition.id,
      target: definition?.target,
      targetEntityId,
    });

    const fallback = isValueEmpty(definition?.defaultValue) ? '' : String(definition.defaultValue);
    const initialValue = isValueEmpty(current?.value) ? fallback : current.value;

    acc[definition.id] = initialValue ?? '';

    return acc;
  }, {});

export const computeDirtyValues = ({ formValues, initialValues, definitions = [] }) => {
  const calculatedDefinitionIds = new Set(
    (definitions ?? []).filter(isCalculated).map(definition => definition.id),
  );

  return Object.keys(formValues ?? {}).reduce((acc, definitionId) => {
    if (calculatedDefinitionIds.has(definitionId)) {
      return acc;
    }

    const next = formValues[definitionId];
    const prev = initialValues?.[definitionId];

    if ((next ?? '') !== (prev ?? '')) {
      acc[definitionId] = next;
    }

    return acc;
  }, {});
};

export const computeFieldErrors = ({ definitions, formValues }) =>
  (definitions ?? []).reduce((acc, definition) => {
    if (isCalculated(definition)) {
      return acc;
    }

    if (isManualReadOnly(definition)) {
      return acc;
    }

    const value = formValues?.[definition.id];

    if (isManualRequired(definition) && isValueEmpty(value)) {
      acc[definition.id] = 'required';

      return acc;
    }

    const formatError = validateValueFormat({ valueType: definition.valueType, value });

    if (formatError) {
      acc[definition.id] = formatError;
    }

    return acc;
  }, {});

export const buildSavePayload = ({ dirtyValues, targetEntityId, definitions = [] }) => {
  const nonSavableDefinitionIds = new Set(
    (definitions ?? [])
      .filter(definition => isManualReadOnly(definition) || isCalculated(definition))
      .map(definition => definition.id),
  );

  return Object.entries(dirtyValues ?? {})
    .filter(([definitionId]) => !nonSavableDefinitionIds.has(definitionId))
    .map(([definitionId, value]) => ({
      definitionId,
      targetEntityId,
      value: isValueEmpty(value) ? null : String(value),
    }));
};

export const buildBackendPendingChangesMap = ({ definitions, values, targetEntityId }) =>
  (definitions ?? []).reduce((acc, definition) => {
    if (isCalculated(definition)) {
      return acc;
    }

    const current = findCurrentValueForDefinition({
      values,
      definitionId: definition.id,
      target: definition?.target,
      targetEntityId,
    });
    const actionType = current?.pendingChangesActionType ?? PendingChangesActionType.NO_CHANGES;

    if (actionType !== PendingChangesActionType.NO_CHANGES) {
      acc[definition.id] = actionType;
    }

    return acc;
  }, {});

const NON_DIFF_PENDING_CHANGE_ACTION_TYPES = [
  PendingChangesActionType.ADDED,
  PendingChangesActionType.DELETED,
];

export const buildCustomFieldsPendingChangesNode = (pendingChangesByDefinition = {}) =>
  Object.entries(pendingChangesByDefinition).reduce(
    (acc, [definitionId, actionType]) => {
      const node = { pendingChangesActionType: actionType, isSeparateChange: true };

      if (!NON_DIFF_PENDING_CHANGE_ACTION_TYPES.includes(actionType)) {
        node.newValue = 'pending';
        node.initialValue = 'initial';
      }

      acc[definitionId] = node;

      return acc;
    },
    { isSeparateChange: true },
  );

export const buildEntitiesCustomFieldsPendingChangesMap = ({ definitions, values, entityIds }) =>
  (entityIds ?? []).reduce((acc, entityId) => {
    if (!entityId) {
      return acc;
    }

    const beMap = buildBackendPendingChangesMap({
      definitions,
      values,
      targetEntityId: entityId,
    });

    if (!Object.keys(beMap).length) {
      return acc;
    }

    acc[entityId] = { customFields: buildCustomFieldsPendingChangesNode(beMap) };

    return acc;
  }, {});
